import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useMap } from 'react-leaflet/hooks';
import { createPortal } from 'react-dom';
import './DataSelector.css';

const DataSelectorMapWrapper = ({ children, onMouseEnter, onMouseLeave, popupOpen }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTransitioning, setIsTransitioning] = useState(false);  
  const map = useMap();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (popupOpen && isMobile) {
      setIsTransitioning(true);
    } else if (!popupOpen) {
      const showTimer = setTimeout(() => {
        setIsTransitioning(false);
      }, 400);
      return () => clearTimeout(showTimer);
    }
  }, [popupOpen, isMobile]);

  const handleMouseEnter = () => {
    if (map) {
      map.doubleClickZoom.disable();
    }
    if (onMouseEnter) onMouseEnter();
  };

  const handleMouseLeave = () => {
    if (map) {
      map.doubleClickZoom.enable();
    }
    if (onMouseLeave) onMouseLeave();
  };

  return (
    <div className={`leaflet-bottom leaflet-left data-selector-map-control ${(popupOpen && isMobile) || isTransitioning ? 'hidden' : ''}`}>
      <div 
        className="leaflet-control data-selector-container"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </div>
  );
};

const DataSelector = ({ 
  selectedData, 
  setSelectedData, 
  locationType,
  popupOpen,
  variant = 'table'
}) => {
  const [showDataMenu, setShowDataMenu] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.data-selector-menu') && !event.target.closest('.data-selector-button')) {
        setShowDataMenu(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  const dataOptions = useMemo(() => {
    let summaryLabel = '';
    
    switch (locationType) {
      case 'city':
        summaryLabel = 'Citywide Summary';
        break;
      case 'county':
        summaryLabel = 'Countywide Summary';
        break;
      case 'state':
        summaryLabel = 'Statewide Summary';
        break;
      case 'metro':
        summaryLabel = 'Metrowide Summary';
        break;
      case 'division':
        summaryLabel = 'Division Summary';
        break;
      case 'region':
        summaryLabel = 'Regional Summary';
        break;
      case 'nation':
        summaryLabel = 'National Summary';
        break;
      default:
        summaryLabel = 'Summary Report';
    }
    
    return [
      summaryLabel,
      'Incident Details',
      'Sex Offenders'
    ];
  }, [locationType]);

  const handleMenuClick = (option) => {
    if (option === selectedData) {
      return;
    }
    
    setSelectedData(option);
    setShowDataMenu(false);
  };

  const MenuComponent = () => {
    if (!showDataMenu || !buttonRef.current) return null;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    
    const menuStyle = variant === 'map' ? {
      left: `${buttonRect.left + (buttonRect.width / 2)}px`,
      transform: 'translateX(-50%)',
      bottom: `${window.innerHeight - buttonRect.top + 2}px`,
      position: 'fixed',
      width: `${buttonRect.width - 4}px` 
    } : {
      left: `${buttonRect.left}px`,
      top: `${buttonRect.bottom + 4}px`,
      position: 'fixed',
      minWidth: `${buttonRect.width}px`
    };

    return (
      <div
        ref={menuRef}
        className={`data-selector-menu ${variant}`}
        style={menuStyle}
      >
        {dataOptions.map((option) => (
          <button 
            key={option} 
            className={`data-selector-item ${selectedData === option ? 'active' : ''}`}
            onClick={() => handleMenuClick(option)}
            disabled={selectedData === option}
          >
            {option}
          </button>
        ))}
      </div>
    );
  };

  const content = (
    <>
      <button 
        ref={buttonRef}
        className={`data-selector-button ${showDataMenu ? 'active' : ''} ${variant}`}
        onClick={() => setShowDataMenu(!showDataMenu)}
      >
        <span>{selectedData}</span>
        <ChevronDown size={20} />
      </button>
      
      {variant === 'map' && showDataMenu && createPortal(
        <MenuComponent />,
        document.body
      )}
      
      {variant === 'table' && showDataMenu && <MenuComponent />}
    </>
  );

  if (variant === 'map') {
    return (
      <DataSelectorMapWrapper popupOpen={popupOpen}>
        {content}
      </DataSelectorMapWrapper>
    );
  }

  return (
    <div className="data-selector-container">
      {content}
    </div>
  );
};

export default DataSelector;
